import {
    Alert,
    AlertIcon,
    Box,
    Button,
    Drawer,
    DrawerBody,
    DrawerCloseButton,
    DrawerContent,
    DrawerFooter,
    DrawerHeader,
    DrawerOverlay,
    Flex,
    Kbd,
    Spinner,
    Text,
    useDisclosure,
} from '@chakra-ui/react';
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { useMemo } from 'react';

import { AssetClass, BlockTradeWithExecution, TradeExecution } from '@/cip/_openapi/portfolio-optimizer';
import {
    useGenerateSchwabAggregatedEquityTrades,
    useGenerateSchwabEquityAllocationFile,
    useGenerateSchwabEquityBlockTradesCsv,
    useGetBlocksAndExecutions,
} from '@/cip/api/trade';
import { expanderCol, numberCol, textCol } from '@/cip/widgets/Table/columnHelpers/colDef';
import { Table } from '@/cip/widgets/Table/Table';
import { TradeSideTag } from '@/cip/widgets/TradeSideTag';

const columnHelper = createColumnHelper<BlockTradeWithExecution>();
const columns = [
    columnHelper.display(expanderCol()),
    columnHelper.accessor('blockTradeId', textCol({ id: 'blockTradeId', header: 'Block Trade ID' })),
    columnHelper.accessor('symbol', textCol({ id: 'symbol', header: 'Symbol' })),
    columnHelper.accessor('side', {
        ...textCol({ id: 'side', header: 'Side' }),
        cell: ({ getValue }) => <TradeSideTag side={getValue()} />,
    }),
    columnHelper.accessor('absTradeQuantity', numberCol({ id: 'absTradeQuantity', header: 'Quantity' })),
    columnHelper.accessor('orderType', textCol({ id: 'orderType', header: 'Order Type' })),
    columnHelper.accessor('limitPrice', numberCol({ id: 'limitPrice', header: 'Limit Price' })),
];

const EMPTY_DATA: BlockTradeWithExecution[] = [];

export const SchwabEquityBlockTradesDrawer = ({
    optimizationId,
    excludedOrderIds,
    isOpen,
    onClose,
}: {
    optimizationId: number;
    excludedOrderIds: string[];
    isOpen: boolean;
    onClose: () => void;
}) => {
    const { data = EMPTY_DATA, isLoading } = useGetBlocksAndExecutions(
        {
            optimizationId,
            assetClass: AssetClass.EQUITY,
        },
        isOpen,
    );

    const schwabEquityBlocks = useMemo(() => data.filter((blockTrade) => blockTrade.brokerId === 'SCHWAB'), [data]);

    const { mutate: generateAggregatedEquityTrades, isPending: isGeneratePending } =
        useGenerateSchwabAggregatedEquityTrades();
    const { mutate: generateBlockTradesCsv, isPending: isBlockDownloadPending } =
        useGenerateSchwabEquityBlockTradesCsv();
    const { mutate: generateAllocationFile, isPending: isAllocationDownloadPending } =
        useGenerateSchwabEquityAllocationFile();

    const table = useReactTable({
        data: schwabEquityBlocks,
        columns,
        enableColumnFilters: false,
        enableSorting: false,
        getCoreRowModel: getCoreRowModel(),
        getRowId: (row) => row.blockTradeId || JSON.stringify(row),
        getRowCanExpand: (row) => row.original.tradeExecutions && row.original.tradeExecutions.length > 0,
    });

    return (
        <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="xl">
            <DrawerOverlay />
            <DrawerContent>
                <DrawerCloseButton />

                <DrawerHeader borderBottomWidth={1}>Schwab Equity Block Trades</DrawerHeader>

                <DrawerBody>
                    <Flex justifyContent="space-between" borderBottomWidth={1} py={3} mb={3}>
                        <Flex gap={2}>
                            <Button
                                colorScheme="teal"
                                size="sm"
                                isLoading={isGeneratePending}
                                onClick={() =>
                                    generateAggregatedEquityTrades({
                                        optimizationId,
                                        requestBody: excludedOrderIds,
                                    })
                                }
                            >
                                Generate block trades
                            </Button>
                            <Button
                                colorScheme="teal"
                                size="sm"
                                isDisabled={!schwabEquityBlocks.length || isGeneratePending}
                                isLoading={isBlockDownloadPending}
                                onClick={() => generateBlockTradesCsv({ optimizationId })}
                            >
                                Download block trades CSV
                            </Button>
                            <Button
                                colorScheme="teal"
                                size="sm"
                                isDisabled={!schwabEquityBlocks.length || isGeneratePending}
                                isLoading={isAllocationDownloadPending}
                                onClick={() => generateAllocationFile({ optimizationId })}
                            >
                                Download allocation file
                            </Button>
                        </Flex>
                        <Flex gap={4} alignItems="center">
                            <Text fontSize="sm" color="gray.500">
                                Block trades:{' '}
                                <Text as="span" fontWeight="semibold" color="gray.200">
                                    {schwabEquityBlocks.length}
                                </Text>
                            </Text>
                            <Text fontSize="sm" color="gray.500">
                                Individual account trades:{' '}
                                <Text as="span" fontWeight="semibold" color="gray.200">
                                    {schwabEquityBlocks.reduce((sum, t) => sum + (t.tradeExecutions?.length ?? 0), 0)}
                                </Text>
                            </Text>
                        </Flex>
                    </Flex>
                    {isLoading ? (
                        <Flex justifyContent="center" alignItems="center" height="100%">
                            <Spinner size="xl" />
                        </Flex>
                    ) : schwabEquityBlocks.length === 0 ? (
                        <Alert status="info">
                            <AlertIcon />
                            <Text>No Schwab equity block trades found for this optimization.</Text>
                        </Alert>
                    ) : (
                        <Table
                            table={table}
                            SubComponent={({ row }) => <TradeExecutionsTable data={row.original.tradeExecutions} />}
                        />
                    )}
                </DrawerBody>

                <DrawerFooter justifyContent="space-between">
                    <Box>
                        <Alert status="info" fontSize="xs">
                            <AlertIcon boxSize={4} />
                            <Text>
                                You can close this panel by hitting <Kbd>Esc</Kbd> or clicking the backdrop
                            </Text>
                        </Alert>
                    </Box>
                    <Button variant="outline" onClick={onClose}>
                        Close
                    </Button>
                </DrawerFooter>
            </DrawerContent>
        </Drawer>
    );
};

export const useSchwabEquityBlockTradesDrawerControls = () => {
    const { isOpen, onOpen, onClose } = useDisclosure();
    return {
        viewSchwabEquityBlockTrades: onOpen,
        isOpen,
        onClose,
    };
};

const tradeExecutionColumnHelper = createColumnHelper<TradeExecution>();
const tradeExecutionColumns = [
    tradeExecutionColumnHelper.accessor('pId', textCol({ id: 'pId', header: 'Portfolio ID' })),
    tradeExecutionColumnHelper.accessor('aC', textCol({ id: 'aC', header: 'Account' })),
    tradeExecutionColumnHelper.accessor('symbol', textCol({ id: 'symbol', header: 'Symbol' })),
    tradeExecutionColumnHelper.accessor('orderType', textCol({ id: 'orderType', header: 'Order Type' })),
    tradeExecutionColumnHelper.accessor('tradeQuantity', numberCol({ id: 'tradeQuantity', header: 'Quantity' })),
    tradeExecutionColumnHelper.accessor('limitPrice', numberCol({ id: 'limitPrice', header: 'Price' })),
];

const TradeExecutionsTable = ({ data }: { data: TradeExecution[] }) => {
    const table = useReactTable({
        data,
        columns: tradeExecutionColumns,
        enableColumnFilters: false,
        enableSorting: false,
        getCoreRowModel: getCoreRowModel(),
    });
    return <Table table={table} />;
};
